import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Grid2,
  useTheme,
  Dialog,
  DialogContent,
  Button,
} from "@mui/material";
import CustomTypography from "../../../components/common/form/CustomTypography";
import AppButton from "../../../components/common/buttons/AppButton";
import Close from "@mui/icons-material/Close";
import CustomTextField from "../../../components/common/textFields/TextField";
import { useStores } from "../../../../use-stores";
import DatePicker2 from "../../../components/common/textFields/DatePicker2";
import dayjs from "dayjs";
import AppSearchDropDown2 from "../../../components/common/filter/AppSearchDropdown2";
import UploadIcon from "../../../../assets/uploadimg.png";
import { observer } from "mobx-react";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  VendorInvoice,
  VendorInvoiceType,
} from "../../../../models/ProjectManagement/InventoryOpsRequests";
import { InvoicePaymentStatusFilters } from "../../../../models/ProjectManagement/ProjectInventoryProduct";
import { formatDate } from "../../../components/common/functions/formatDate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { faFilePdf } from "@fortawesome/free-regular-svg-icons";

const InvoiceInformationPopup: React.FC<InvoiceInformationPopupProps> = ({
  inwardId,
  type,
  open,
  onClose,
  onSaveInvoiceDetails,
}) => {
  const theme = useTheme();
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 600);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: { xs: 0, sm: "16px" },
            boxShadow: {
              xs: "none",
              sm: "0 20px 40px rgba(0, 0, 0, 0.15)",
            },
            overflow: "hidden",
            maxHeight: { xs: "100vh", sm: "90vh" },
            maxWidth: { xs: "100vw", sm: "1200px" },
            width: "100%",
            margin: { xs: 0, sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: { xs: "stretch", sm: "center" },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "16px", sm: "16px 20px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
            minHeight: { xs: "56px", sm: "auto" },
          }}
        >
          <CustomTypography
            variant="heading3"
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              fontWeight: 600,
              lineHeight: 1.2,
            }}
          >
            Vendor Invoices
          </CustomTypography>
          <IconButton
            onClick={handleClose}
            size="medium"
            sx={{
              color: theme.palette.customColors.greyTextColor,
              padding: { xs: "8px", sm: "12px" },
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <Close sx={{ fontSize: { xs: "20px", sm: "24px" } }} />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "16px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            maxHeight: { xs: "calc(100vh - 120px)", sm: "70vh" },
            minHeight: { xs: "calc(100vh - 120px)", sm: "auto" },
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors.greyBackground,
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors.greyDarkborder,
              borderRadius: "3px",
              "&:hover": {
                backgroundColor: theme.palette.customColors.greyTextColor,
              },
            },
          }}
        >
          <InvoiceInformation
            inwardId={inwardId}
            type={type}
            onSaveInvoiceDetails={onSaveInvoiceDetails}
          />
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default observer(InvoiceInformationPopup);

// InvoiceInformation Component
interface InvoiceInformationProps {
  inwardId: number;
  type: VendorInvoiceType;
  onSaveInvoiceDetails: (
    inwardId: number,
    type: VendorInvoiceType,
    invoiceReceived: boolean,
    invoices?: VendorInvoice[]
  ) => void;
}

// InvoiceInformationPopup Component
interface InvoiceInformationPopupProps {
  inwardId: number;
  type: VendorInvoiceType;
  open: boolean;
  onClose: () => void;
  onSaveInvoiceDetails: (
    inwardId: number,
    type: VendorInvoiceType,
    invoiceReceived: boolean,
    invoices?: VendorInvoice[]
  ) => void;
}

const InvoiceInformation: React.FC<InvoiceInformationProps> = ({
  inwardId,
  type,
  onSaveInvoiceDetails,
}) => {
  const theme = useTheme();

  const { projectInventoryOpsStore } = useStores();

  type VendorInvoiceWithFiles = VendorInvoice & { files?: File[] };
  const [isInvoiceReceived, setIsInvoiceReceived] = useState<boolean>(
    projectInventoryOpsStore.vendorInvoiceDetails.invoice_received || false
  );
  const [invoices, setInvoices] = useState<VendorInvoiceWithFiles[]>(
    projectInventoryOpsStore.vendorInvoiceDetails
      .invoices as VendorInvoiceWithFiles[]
  );

  // Clear files on row when upload URL becomes available
  useEffect(() => {
    setInvoices((prev) =>
      prev.map((inv) => (inv.invoice_url ? { ...inv } : inv))
    );
  }, [invoices]);

  const handleSelectInvoiceFiles = (
    rowIndex: number,
    filesList: FileList | null
  ) => {
    if (!filesList) return;
    const files = Array.from(filesList);
    setInvoices((prev) =>
      prev.map((invoice, idx) =>
        idx === rowIndex ? { ...invoice, files } : invoice
      )
    );
  };

  // Add new invoice
  const addInvoice = () => {
    const newInvoice: VendorInvoiceWithFiles = {
      serial_number: invoices.length + 1,
      type: type,
      inward_id: inwardId,
      created_by: 0,
      invoice_number: "",
      invoice_date: undefined,
      invoice_status: undefined,
      invoice_url: undefined,
      files: undefined,
    };
    setInvoices([...invoices, newInvoice]);
  };

  // Remove invoice
  const removeInvoice = (serial_number?: number, id?: number) => {
    if (id) {
      setInvoices(invoices.filter((invoice) => invoice.id !== id));
    } else if (serial_number) {
      setInvoices(
        invoices.filter((invoice) => invoice.serial_number !== serial_number)
      );
    }
  };

  // Update invoice data
  const updateInvoice = (
    id: number | undefined,
    serial_number: number | undefined,
    field: keyof VendorInvoice,
    value: any
  ) => {
    if (id) {
      setInvoices(
        invoices.map((invoice) =>
          invoice.id === id
            ? {
                ...invoice,
                [field]: value,
              }
            : invoice
        )
      );
    } else if (serial_number) {
      setInvoices(
        invoices.map((invoice) =>
          invoice.serial_number === serial_number
            ? {
                ...invoice,
                [field]: value,
              }
            : invoice
        )
      );
    }
  };

  // Save all invoices
  const handleSaveAll = () => {
    onSaveInvoiceDetails(inwardId, type, isInvoiceReceived, invoices);
  };

  return (
    <Box>
      <CustomTypography
        variant="body2"
        sx={{
          fontWeight: 600,
          marginBottom: { xs: 12, sm: 16 },
          color: theme.palette.customColors.blackBorder,
          fontSize: { xs: "0.875rem", sm: "1rem" },
        }}
      >
        Is the invoice received?
      </CustomTypography>

      <Box
        sx={{
          display: "flex",
          gap: { xs: 1, sm: 2 },
          marginTop: 1,
          marginBottom: { xs: 2, sm: 2 },
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "flex-start" },
        }}
      >
        <AppButton
          label="Yes"
          onClick={() => {
            if (isInvoiceReceived === false) {
              setIsInvoiceReceived(true);
            }
          }}
          customStyles={{
            backgroundColor: isInvoiceReceived
              ? theme.palette.customColors.EmeraldGreen
              : theme.palette.customColors.greyDarkborder,
            color: theme.palette.customColors.white,
            fontWeight: 600,
            padding: { xs: "4px 12px", sm: "6px 12px" },
            borderRadius: "8px",
            fontSize: "0.9rem",
            minHeight: { xs: "38px", sm: "auto" },
            "&:hover": {
              backgroundColor: isInvoiceReceived
                ? theme.palette.customColors.greenDark
                : theme.palette.customColors.greyTextColor,
            },
          }}
        />
        <AppButton
          label="No"
          onClick={() => {
            if (isInvoiceReceived === true) {
              setIsInvoiceReceived(false);
            }
          }}
          customStyles={{
            backgroundColor: !isInvoiceReceived
              ? theme.palette.customColors.EmeraldGreen
              : theme.palette.customColors.greyDarkborder,
            color: theme.palette.customColors.white,
            fontWeight: 600,
            padding: { xs: "4px 12px", sm: "6px 12px" },
            borderRadius: "8px",
            fontSize: "0.9rem",
            minHeight: { xs: "38px", sm: "auto" },
            "&:hover": {
              backgroundColor: !isInvoiceReceived
                ? theme.palette.customColors.greenDark
                : theme.palette.customColors.greyTextColor,
            },
          }}
        />
      </Box>

      {/* Invoice Details Form - Show when Yes is selected */}
      {Boolean(isInvoiceReceived) && (
        <Box
          sx={{
            padding: { xs: 2, sm: 3 },
            backgroundColor: theme.palette.customColors.lightGreyColor,
            borderRadius: { xs: "8px", sm: "12px" },
            border: `1px solid ${theme.palette.customColors.greyBorder}`,
            marginTop: { xs: 1, sm: 2 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              marginBottom: { xs: 2, sm: 4 },
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 0 },
            }}
          >
            <CustomTypography
              variant="heading6"
              sx={{
                fontWeight: 600,
                color: theme.palette.customColors.blackBorder,
                fontSize: { xs: "1rem", sm: "1.125rem" },
              }}
            >
              {invoices.length === 0
                ? "Invoice Information"
                : `Invoice Information (${invoices.length} invoice${
                    invoices.length !== 1 ? "s" : ""
                  }) 
              `}
            </CustomTypography>
            <AppButton
              label="Add Invoice"
              onClick={addInvoice}
              customStyles={{
                backgroundColor: theme.palette.customColors.blueGradientStart,
                color: theme.palette.customColors.white,
                fontWeight: 600,
                padding: { xs: "4px 12px", sm: "6px 12px" },
                borderRadius: "8px",
                minWidth: "auto",
                fontSize: "0.9rem",
                minHeight: { xs: "38px", sm: "auto" },
                alignSelf: { xs: "stretch", sm: "auto" },
                "&:hover": {
                  backgroundColor:
                    theme.palette.customColors.blueGradientHoverStart,
                  transform: "translateY(-1px)",
                },
              }}
            />
          </Box>

          {/* Invoice List */}
          {invoices.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: { xs: 2, sm: 3 },
                border: `2px dashed ${theme.palette.customColors.greyBorder}`,
                borderRadius: "8px",
                backgroundColor: theme.palette.customColors.white,
                minHeight: { xs: "120px", sm: "140px" },
              }}
            >
              <CustomTypography
                variant="body2"
                style={{
                  color: theme.palette.customColors.greyTextColor,
                  marginBottom: 16,
                }}
              >
                No invoices added yet
              </CustomTypography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {invoices.map((invoice, index) => (
                <Box
                  key={invoice.id}
                  sx={{
                    padding: { xs: 2, sm: 3 },
                    backgroundColor: theme.palette.customColors.white,
                    borderRadius: "8px",
                    border: `1px solid ${theme.palette.customColors.greyBorder}`,
                    position: "relative",
                  }}
                >
                  {/* Invoice Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: { xs: 1.5, sm: 2 },
                    }}
                  >
                    <CustomTypography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.customColors.blackBorder,
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      }}
                    >
                      Invoice #{index + 1}
                    </CustomTypography>
                    <IconButton
                      onClick={() =>
                        removeInvoice(invoice.serial_number, invoice.id)
                      }
                      size="small"
                      sx={{
                        color: theme.palette.customColors.pinkDark,
                        padding: { xs: "4px", sm: "8px" },
                        "&:hover": {
                          backgroundColor: theme.palette.customColors.pinkLight,
                        },
                      }}
                    >
                      <DeleteIcon
                        sx={{ fontSize: { xs: "16px", sm: "20px" } }}
                      />
                    </IconButton>
                  </Box>

                  <Grid2 container spacing={{ xs: 1.5, sm: 2 }}>
                    {/* Invoice Number */}
                    <Grid2 size={{ xs: 12, md: 4 }}>
                      <CustomTextField
                        width="100%"
                        borderRadius="4px"
                        label="Invoice Number"
                        placeholder="Enter invoice number"
                        value={invoice.invoice_number}
                        onChange={(e) =>
                          updateInvoice(
                            invoice.id,
                            invoice.serial_number,
                            "invoice_number",
                            e.target.value
                          )
                        }
                      />
                    </Grid2>

                    {/* Invoice Date */}
                    <Grid2 size={{ xs: 12, md: 4 }}>
                      <DatePicker2
                        type="date"
                        borderRadius="4px"
                        label="Invoice Date"
                        value={
                          invoice.invoice_date
                            ? dayjs(invoice.invoice_date)
                            : null
                        }
                        onChange={(date) =>
                          updateInvoice(
                            invoice.id,
                            invoice.serial_number,
                            "invoice_date",
                            date
                          )
                        }
                      />
                    </Grid2>
                    {/* Payment Status */}
                    <Grid2 size={{ xs: 12, md: 4 }}>
                      <AppSearchDropDown2
                        borderRadius="4px"
                        showSelection={true}
                        isMultiSelect={false}
                        title="Select Payment Status"
                        fontSize="0.9rem"
                        value={
                          invoice.invoice_status
                            ? [
                                InvoicePaymentStatusFilters.find(
                                  (status) =>
                                    status.id === invoice.invoice_status
                                )!,
                              ]
                            : []
                        }
                        list={InvoicePaymentStatusFilters}
                        local={true}
                        handleSelect={(items) => {
                          if (items.length > 0) {
                            updateInvoice(
                              invoice.id,
                              invoice.serial_number,
                              "invoice_status",
                              items[0].id
                            );
                          } else {
                            updateInvoice(
                              invoice.id,
                              invoice.serial_number,
                              "invoice_status",
                              undefined
                            );
                          }
                        }}
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2
                    container
                    spacing={{ xs: 1.5, sm: 2 }}
                    sx={{
                      marginTop: { xs: 2, md: 4 },
                      justifyContent: "flex-end",
                    }}
                  >
                    {/* Invoice Upload */}
                    <Grid2 size={{ xs: 12, md: 4 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <input
                          type="file"
                          multiple
                          onChange={(event) =>
                            handleSelectInvoiceFiles(index, event.target.files)
                          }
                          style={{ display: "none" }}
                          id={`fileInput-${index}`}
                        />
                        <CustomTypography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: theme.palette.customColors.blackBorder,
                            fontSize: { xs: "0.875rem", sm: "1rem" },
                          }}
                        >
                          Upload Invoice <span style={{ color: "red" }}>*</span>
                        </CustomTypography>
                        <Button
                          sx={{ width: "100%", height: "100%", padding: 0 }}
                          onClick={() =>
                            document
                              .getElementById(`fileInput-${index}`)
                              ?.click()
                          }
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: { xs: 1, sm: 2 },
                              padding: { xs: 1.5, sm: 2 },
                              border: `2px dashed ${theme.palette.customColors.greyBorder}`,
                              borderRadius: "4px",
                              backgroundColor: theme.palette.customColors.white,
                              cursor: "pointer",
                              transition: "all 0.1s ease-in-out",
                              minHeight: { xs: "48px", sm: "auto" },
                              width: "100%",
                              height: "100%",
                              "&:hover": {
                                borderColor:
                                  theme.palette.customColors.purpleMain,
                                backgroundColor:
                                  theme.palette.customColors.lightPurple,
                              },
                            }}
                          >
                            <img
                              src={UploadIcon}
                              alt="Upload"
                              style={{
                                width: 20,
                                height: 20,
                                minWidth: 20,
                                minHeight: 20,
                              }}
                            />
                            <CustomTypography
                              variant="body2"
                              sx={{
                                color: theme.palette.customColors.greyTextColor,
                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {(invoice.files as File[] | undefined)
                                ?.map((fileItem: File) => fileItem.name)
                                .join(", ") || "Click to upload invoice file"}
                            </CustomTypography>
                          </Box>
                        </Button>
                        {invoice.invoice_url && (
                          <Box
                            sx={{
                              marginTop: 1,
                              display: "flex",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            {invoice.invoice_url
                              .split(",")
                              .map((rawUrl, idx) => {
                                const url = rawUrl.trim();
                                if (!url) return null;
                                return (
                                  <Box
                                    key={`${url}-${idx}`}
                                    sx={{
                                      width: 64,
                                      height: 64,
                                      border: `1px solid ${theme.palette.customColors.greyBorder}`,
                                      borderRadius: "4px",
                                      overflow: "hidden",
                                      backgroundColor:
                                        theme.palette.customColors.white,
                                      position: "relative",
                                    }}
                                  >
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        display: "block",
                                        width: "100%",
                                        height: "100%",
                                      }}
                                    >
                                      {url.toLowerCase().endsWith(".pdf") ? (
                                        <Box
                                          sx={{
                                            width: "100%",
                                            height: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon={faFilePdf}
                                            style={{
                                              fontSize: 28,
                                              opacity: 0.7,
                                            }}
                                          />
                                        </Box>
                                      ) : (
                                        <img
                                          src={url}
                                          alt={`Invoice ${idx + 1}`}
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",
                                          }}
                                          onError={(e) => {
                                            // fallback: hide broken image preview but keep link clickable
                                            (
                                              e.currentTarget as HTMLImageElement
                                            ).style.display = "none";
                                          }}
                                        />
                                      )}
                                    </a>
                                    <IconButton
                                      component="a"
                                      href={url}
                                      download
                                      sx={{
                                        position: "absolute",
                                        top: 4,
                                        right: 4,
                                        width: 10,
                                        height: 10,
                                        backgroundColor:
                                          "rgba(255,255,255,0.5)",
                                        "&:hover": {
                                          backgroundColor:
                                            "rgba(255,255,255,1)",
                                        },
                                        boxShadow: 1,
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <FontAwesomeIcon
                                        icon={faDownload}
                                        style={{ fontSize: 12, opacity: 0.7 }}
                                      />
                                    </IconButton>
                                  </Box>
                                );
                              })}
                          </Box>
                        )}
                      </Box>
                    </Grid2>
                  </Grid2>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 2,
        }}
      >
        {projectInventoryOpsStore.vendorInvoiceDetails
          .invoice_received_updated_by_name &&
        projectInventoryOpsStore.vendorInvoiceDetails
          .invoice_received_updated_at ? (
          <CustomTypography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: theme.palette.customColors.blackBorder,
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            Last Updated By:{" "}
            {
              projectInventoryOpsStore.vendorInvoiceDetails
                .invoice_received_updated_by_name
            }{" "}
            on{" "}
            {formatDate(
              projectInventoryOpsStore.vendorInvoiceDetails
                .invoice_received_updated_at
            )}
          </CustomTypography>
        ) : (
          <CustomTypography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: theme.palette.customColors.blackBorder,
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            Last Updated By: No Info Available
          </CustomTypography>
        )}
        <AppButton
          label="Save Invoice Details"
          onClick={handleSaveAll}
          customStyles={{
            backgroundColor: theme.palette.customColors.EmeraldGreen,
            color: theme.palette.customColors.white,
            fontWeight: 600,
            padding: { xs: "4px 12px", sm: "6px 12px" },
            borderRadius: "8px",
            fontSize: "0.9rem",
            minHeight: { xs: "38px", sm: "auto" },
            "&:hover": {
              backgroundColor: theme.palette.customColors.greenDark,
            },
          }}
        />
      </Box>
    </Box>
  );
};
